import { MobXProviderContext } from "mobx-react";
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { appDataTableWrapperStyle, appInnerCardStyle, appMutedTextStyle, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { combineLosAngelesDateAndTime } from "../../../utils/losAngelesDateTime";
import FacilityTasksPage from "../../home/pages/FacilityTasksPage";

const TIMEZONE = "America/Los_Angeles";
const todayKey = () => new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
const dateKeyFromDate = (date: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
const getWeekStartKey = (dateValue: string) => {
  const date = new Date(`${dateValue}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return new Intl.DateTimeFormat("en-CA", { timeZone: TIMEZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
};
const formatStatus = (value: any) => String(value || "pending").replace("_", " ").replace(/\b\w/g, (character) => character.toUpperCase());
const formatDateTime = (value: any) => {
  const date = new Date(String(value || ""));
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("en-US", { timeZone: TIMEZONE, dateStyle: "medium", timeStyle: "short" });
};
const formatTaskDateTime = (dateValue: any, timeValue?: any) => {
  const date = String(dateValue || "").trim();
  const time = String(timeValue || "").trim();
  if (!date) return "—";
  if (!/^\d{2}:\d{2}$/.test(time)) return date;
  const [hours, minutes] = time.split(":").map(Number);
  return `${date} ${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours >= 12 ? "PM" : "AM"}`;
};
const formatScheduledTaskTime = (value: any) => {
  const rawValue = String(value || "").trim();
  const timeMatch = /(?:T|\s)(\d{2}:\d{2})(?::\d{2})?(?:Z|$)/.exec(rawValue);
  const time = /^\d{2}:\d{2}$/.test(rawValue) ? rawValue : String(timeMatch?.[1] || "");
  if (!/^\d{2}:\d{2}$/.test(time)) return "";
  const [hours, minutes] = time.split(":").map(Number);
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours >= 12 ? "PM" : "AM"}`;
};
const taskDateKey = (row: any) => String(row?.occurrenceDate || row?.startDate || "9999-12-31").slice(0, 10);
const formatTaskDateGroup = (dateKey: string) => new Date(`${dateKey}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: TIMEZONE, weekday: "long", month: "long", day: "numeric", year: "numeric" });
const sortTasksByDateTime = (rows: any[]) => [...rows].sort((first, second) => {
  const firstKey = `${taskDateKey(first)}T${String(first?.scheduledTimes?.[0] || "00:00")}`;
  const secondKey = `${taskDateKey(second)}T${String(second?.scheduledTimes?.[0] || "00:00")}`;
  return firstKey.localeCompare(secondKey) || String(first?.title || "").localeCompare(String(second?.title || ""));
});
const getTaskRowKey = (row: any) => String(row?.taskId || row?.id || `${row?.title || "task"}-${row?.occurrenceDate || row?.startDate || ""}-${row?.scheduledTimes?.[0] || ""}`);

const StaffFacilityTasksEmptyState = () => (
  <div className="flex flex-column align-items-center text-center py-4">
    <div className="font-semibold text-xl text-900">No tasks assigned to you for this day</div>
    <div className="text-color-secondary mt-2" style={{ maxWidth: "42rem" }}>
      Facility tasks assigned to your staff account will appear here. Ask your provider or home administrator to assign a task when you are ready to get started.
    </div>
  </div>
);

export const StaffFacilityTasksPage = ({ assignedToMe = true, showReminder = false, onEditTask }: { assignedToMe?: boolean; showReminder?: boolean; onEditTask?: (row: any) => void }) => {
  const { homeId } = useParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const mobile = useResponsive().isMobile;
  const toast = useRef<Toast>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [dateKey] = useState(todayKey());
  const [taskView, setTaskView] = useState<"upcoming" | "previous">("upcoming");
  const [taskCursor, setTaskCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState("");
  const [editingStatusRow, setEditingStatusRow] = useState<any | null>(null);
  const [draftStatus, setDraftStatus] = useState("pending");
  const [statusDialogNote, setStatusDialogNote] = useState("");
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  const [exportWeekStart, setExportWeekStart] = useState(getWeekStartKey(todayKey()));
  const [exportView, setExportView] = useState<"list" | "grid">("grid");
  const [exporting, setExporting] = useState(false);
  const [hasPreviousTasks, setHasPreviousTasks] = useState(false);
  const [hasMoreTasks, setHasMoreTasks] = useState(false);
  const [calendarSavingTaskId, setCalendarSavingTaskId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const load = async (append = false) => {
    if (!homeId) return;
    setLoading(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getFacilityTasks(homeId, { dateKey, assignedToMe, staffPass: !assignedToMe, view: taskView, cursor: append ? taskCursor : null, pageSize: 100, sortField: "time", sortOrder: "asc" });
      const nextTasks = Array.isArray(response?.tasks) ? response.tasks : [];
      setTasks((current) => {
        const nextTaskList = append ? [...current, ...nextTasks] : nextTasks;
        const pendingCount = nextTaskList.filter((task: any) => String(task?.status || "pending").trim().toLowerCase() === "pending").length;
        window.dispatchEvent(new CustomEvent("facility-tasks:availability", { detail: { count: nextTaskList.length, pendingCount } }));
        return nextTaskList;
      });
      setTaskCursor(response?.nextCursor || null);
      setHasPreviousTasks(Boolean(response?.previousToken));
      setHasMoreTasks(Boolean(response?.hasMore));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Facility Tasks", detail: error?.message || "Unable to load assigned tasks." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setTaskCursor(null); void load(false); }, [homeId, dateKey, taskView]);

  useEffect(() => {
    const handleRefresh = () => {
      setTaskCursor(null);
      void load(false);
    };
    window.addEventListener("facility-tasks:refresh", handleRefresh);
    return () => window.removeEventListener("facility-tasks:refresh", handleRefresh);
  }, [homeId, dateKey, taskView, assignedToMe]);

  useEffect(() => {
    const handleOpenExport = () => {
      setExportWeekStart(getWeekStartKey(dateKey));
      setExportDialogVisible(true);
    };
    window.addEventListener("facility-tasks:open-export", handleOpenExport);
    return () => window.removeEventListener("facility-tasks:open-export", handleOpenExport);
  }, [dateKey]);

  const updateStatus = async (row: any, status: string, notes = "") => {
    if (!homeId || !row?.taskId || !status) return;
    if (row?.canChart === false) {
      toast.current?.show({ severity: "warn", summary: "Charting unavailable", detail: "This task cannot be charted yet. It becomes available within 1 hour before the scheduled time." });
      return;
    }
    const noteValue = String(notes || "").trim();
    if (status !== "completed" && !noteValue) {
      toast.current?.show({ severity: "warn", summary: "Note required", detail: "Add a note before saving this task status." });
      return;
    }
    setUpdatingTaskId(String(row.taskId));
    try {
      await backendAPIStore.updateFacilityTaskStatus({ homeId, taskId: row.taskId, dateKey, status, notes: noteValue });
      toast.current?.show({ severity: "success", summary: "Status updated", detail: "The task status was updated." });
      setEditingStatusRow(null);
      setStatusDialogNote("");
      await load(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Status update failed", detail: error?.message || "Unable to update task status." });
    } finally {
      setUpdatingTaskId("");
    }
  };

  const createTaskCalendarReminder = async (row: any) => {
    if (!homeId) return;
    const taskId = String(row?.taskId || row?.title || "");
    setCalendarSavingTaskId(taskId);
    try {
      const startAt = combineLosAngelesDateAndTime(taskDateKey(row), row?.scheduledTimes?.[0] || "09:00");
      if (!startAt) throw new Error("Choose a valid task date and time first.");
      const lookups = await backendAPIStore.getSharedCalendarLookups();
      const workspaceId = lookups?.workspaceOptions?.[0]?.value;
      if (!workspaceId) throw new Error("No calendar workspace is available for this home.");
      await backendAPIStore.createSharedCalendarEvent({
        workspaceId,
        eventType: "general_reminder",
        title: String(row?.title || "Facility task"),
        description: String(row?.instructions || ""),
        startAt,
        homeId,
        residentId: row?.residentId || undefined,
        allDay: false,
        notificationSettings: { emailReminder: "none" },
        recurrenceRule: null,
      });
      toast.current?.show({ severity: "success", summary: "Reminder added", detail: "A calendar reminder was created." });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Reminder failed", detail: error?.message || "Unable to create the calendar reminder." });
    } finally {
      setCalendarSavingTaskId("");
    }
  };

  const exportReport = async () => {
    if (!homeId) return;
    setExporting(true);
    try {
      const response = await backendAPIStore.exportFacilityTaskWeeklyActivity({ homeId, staffId: authStore.getUserId(), weekStart: exportWeekStart, view: exportView });
      if (response?.documentUrl) window.open(response.documentUrl, "_blank", "noopener,noreferrer");
      toast.current?.show({ severity: "success", summary: "Export ready", detail: "Your activity report opened in a new tab." });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Export failed", detail: error?.message || "Unable to export your activity report." });
    } finally {
      setExporting(false);
    }
  };

  const categoryOptions = useMemo(() => [{ label: "All categories", value: "" }, ...Array.from(new Set(tasks.map((item) => String(item?.category || "Other")).filter(Boolean))).sort().map((value) => ({ label: value, value }))], [tasks]);
  const visibleTasks = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return sortTasksByDateTime(tasks.filter((item) => {
      if (categoryFilter && String(item?.category || "Other") !== categoryFilter) return false;
      if (!normalizedSearch) return true;
      return [item?.title, item?.instructions, item?.category, item?.residentName].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));
    }));
  }, [categoryFilter, searchText, tasks]);
  const renderStatus = (row: any) => {
    const status = String(row?.status || "pending").toLowerCase();
    const statusLabel = status === "pending" && row?.canChart === false ? "Upcoming" : formatStatus(status);
    const statusStyle: Record<string, any> = {
      completed: { background: "#689f38", borderColor: "#689f38", color: "#ffffff" },
      pending: { background: "#f59e0b", borderColor: "#f59e0b", color: "#ffffff" },
      upcoming: { background: "#f59e0b", borderColor: "#f59e0b", color: "#ffffff" },
      not_completed: { background: "#dc2626", borderColor: "#dc2626", color: "#ffffff" },
      skipped: { background: "#f59e0b", borderColor: "#f59e0b", color: "#ffffff" },
    };
    return <div className="flex flex-column gap-1">
      <Tag value={`${statusLabel}${formatScheduledTaskTime(row?.scheduledTimes?.[0] || row?.time) ? ` ${formatScheduledTaskTime(row?.scheduledTimes?.[0] || row?.time)}` : ""}`} rounded style={{ ...statusStyle[status], borderStyle: "solid", borderWidth: "1px", borderRadius: "999px", backgroundClip: "padding-box", fontSize: "0.78rem", fontWeight: 700, lineHeight: 1.2, padding: "0.35rem 0.65rem", width: "fit-content", whiteSpace: "nowrap", fontFamily: "inherit" }} />
      {row?.completionNotes ? <div style={{ ...appMutedTextStyle, fontSize: "0.78rem", whiteSpace: "pre-wrap", maxWidth: "18rem" }}>{row.completionNotes}</div> : null}
    </div>;
  };
  const taskStatusActions = [
    { label: "Completed", value: "completed" },
    { label: "Not Completed", value: "not_completed" },
    { label: "Skipped", value: "skipped" },
  ];
  const openTaskStatusAction = (row: any, status: string) => {
    if (row?.canChart === false) {
      toast.current?.show({ severity: "warn", summary: "Charting unavailable", detail: "This task cannot be charted yet. It becomes available within 1 hour before the scheduled time." });
      return;
    }
    if (status === "completed") {
      void updateStatus(row, status);
      return;
    }
    setEditingStatusRow(row);
    setDraftStatus(status);
    setStatusDialogNote("");
  };
  const renderTaskActions = (row: any) => {
    const currentStatus = String(row?.status || "pending").toLowerCase();
    const canChart = row?.canChart !== false;
    const actionStyles: Record<string, any> = {
      completed: { background: currentStatus === "completed" ? "#dcfce7" : "#ffffff", borderColor: "#166534", color: "#166534" },
      not_completed: { background: currentStatus === "not_completed" ? "#fee2e2" : "#ffffff", borderColor: "#991b1b", color: "#991b1b" },
      skipped: { background: currentStatus === "skipped" ? "#fef3c7" : "#ffffff", borderColor: "#92400e", color: "#92400e" },
    };
    return <div className="flex flex-column gap-2" style={{ minWidth: "16rem" }}>
      <div className="flex flex-wrap gap-2">
        {taskStatusActions.map((action) => <Button key={action.value} type="button" label={action.label} className="facility-task-action-button" rounded disabled={!canChart} aria-label={`Set task status to ${action.label}`} style={{ ...actionStyles[action.value], opacity: !canChart ? 0.7 : 1, boxShadow: "none", border: `1px solid ${actionStyles[action.value].borderColor}`, borderRadius: "999px", fontSize: "0.78rem", fontWeight: 700, padding: "0.35rem 0.65rem", cursor: canChart ? "pointer" : "not-allowed", whiteSpace: "nowrap", fontFamily: "inherit" }} onClick={() => openTaskStatusAction(row, action.value)} />)}
      </div>
      {!canChart ? (
        <div style={{ ...appMutedTextStyle, fontSize: "0.75rem" }}>Future charting will open 1 hour before the scheduled time.</div>
      ) : null}
    </div>;
  };
  const renderLastUpdated = (row: any) => formatDateTime(row.modifiedAt || row.createdAt);
  const renderTaskDetails = (row: any) => {
    const assignees = Array.isArray(row?.assignedStaffNames) && row.assignedStaffNames.length
      ? row.assignedStaffNames.join(", ")
      : "Unassigned";
    const scope = String(row?.scope || "facility").toLowerCase() === "resident" ? (row?.residentName || "Resident") : "Facility";
    return <div style={{ minWidth: 0 }}>
      <div style={{ minWidth: 0, flex: "1 1 auto", overflowWrap: "anywhere" }}>
        <div className="flex align-items-center gap-2" style={{ fontSize: "0.92rem", fontWeight: 800, lineHeight: 1.3, color: "#0f172a" }}>
          <span style={{ overflowWrap: "anywhere" }}>{row?.title || "Facility Task"}</span>
          {onEditTask ? <Button type="button" icon="pi pi-pencil" severity="warning" outlined rounded size="small" style={{ width: "1.8rem", height: "1.8rem", flex: "0 0 auto", color: "#ea580c", background: "#fff7ed" }} tooltip="Edit task" aria-label={`Edit ${row?.title || "task"}`} onClick={() => onEditTask(row)} /> : null}
        </div>
        {row?.instructions ? <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.78rem", lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{row.instructions}</div> : null}
        <div className="mt-2 flex flex-column gap-1" style={{ color: "#64748b", fontSize: "0.75rem", lineHeight: 1.45 }}>
          <span><strong>Assignee:</strong> {assignees}</span>
          <span><strong>Scope:</strong> {scope}</span>
          <span><strong>Last Updated:</strong> {renderLastUpdated(row)}</span>
        </div>
      </div>
    </div>;
  };

  return <div className="w-full p-component">
    <Toast ref={toast} />
    <style>{`.facility-task-action-button .p-button-label { color: inherit !important; }`}</style>
    <div style={{ ...appInnerCardStyle, background: "#ffffff", padding: "1rem 1.1rem", width: "100%" }}>
      <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
        <div style={{ minWidth: 0 }}>{renderSurfaceHeader("Today's Pass", assignedToMe ? "Review and update tasks assigned to you." : "Review and update today's facility task pass.")}</div>
        <div className="flex align-items-center justify-content-end gap-2 flex-wrap" style={{ marginLeft: "auto" }}>
          {tasks.length ? <>
            <InputText value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search" aria-label="Search facility tasks" className="w-12rem" />
            <Dropdown value={categoryFilter} options={categoryOptions} optionLabel="label" optionValue="value" onChange={(event) => setCategoryFilter(event.value || "")} placeholder="Category" className="w-10rem" />
          </> : null}
        </div>
      </div>
      {!loading && !visibleTasks.length ? <StaffFacilityTasksEmptyState /> : mobile ? <div className="flex flex-column gap-3 mt-3">
        {loading ? <ProgressSpinner className="align-self-center" /> : null}
        {!loading && !visibleTasks.length ? <div className="text-center p-3">No tasks assigned to you for this day.</div> : null}
        {visibleTasks.map((row: any) => <Fragment key={row.taskId}>
          <div style={{ ...appInnerCardStyle, padding: "1rem" }}>
          {renderTaskDetails(row)}
          <div className="flex flex-column gap-2 mt-3">
            <div>{renderStatus(row)}</div>
          </div>
          <div className="mt-3">{renderTaskActions(row)}</div>
          {showReminder ? <div className="flex justify-content-end mt-2"><Button type="button" icon="pi pi-bell" severity="warning" outlined rounded loading={calendarSavingTaskId === String(row.taskId)} tooltip="Add calendar reminder" aria-label={`Set reminder for ${row.title}`} onClick={() => void createTaskCalendarReminder(row)} /></div> : null}
          </div>
        </Fragment>)}
      </div> : <div className="mt-3" style={appDataTableWrapperStyle}><DataTable value={visibleTasks} loading={loading} dataKey="taskId" emptyMessage="No tasks assigned to you for this day." responsiveLayout="scroll" stripedRows size="small">
          <Column header="Task" style={{ width: "42%", minWidth: "20rem" }} bodyStyle={{ verticalAlign: "top" }} body={renderTaskDetails} />
          <Column header="Status" style={{ width: "18rem" }} bodyStyle={{ verticalAlign: "top" }} body={renderStatus} />
          <Column header="Actions" style={{ width: "40%", minWidth: "22rem" }} bodyStyle={{ verticalAlign: "top" }} body={(row: any) => <div className="flex flex-column gap-2">{renderTaskActions(row)}{showReminder ? <Button type="button" icon="pi pi-bell" severity="warning" outlined rounded loading={calendarSavingTaskId === String(row.taskId)} tooltip="Add calendar reminder" aria-label={`Set reminder for ${row.title}`} onClick={() => void createTaskCalendarReminder(row)} /> : null}</div>} />
        </DataTable></div>}
      <div className="flex justify-content-center gap-2 mt-3">
        {hasPreviousTasks ? <Button type="button" label="Load Previous" icon="pi pi-chevron-up" severity="warning" outlined rounded disabled={loading} onClick={() => setTaskView("previous")} /> : null}
        {hasMoreTasks ? <Button type="button" label="Load More" icon="pi pi-chevron-down" iconPos="right" severity="warning" outlined rounded disabled={loading} onClick={() => void load(true)} /> : null}
      </div>
    </div>
    <Dialog
      visible={Boolean(editingStatusRow)}
      modal
      header="Add Note"
      style={{ width: "min(94vw, 30rem)" }}
      onHide={() => !updatingTaskId && setEditingStatusRow(null)}
      footer={<div className="flex justify-content-end gap-2">
        <Button type="button" label="Cancel" severity="secondary" outlined disabled={Boolean(updatingTaskId)} onClick={() => setEditingStatusRow(null)} />
        <Button type="button" label="Save" icon="pi pi-check" severity="warning" rounded loading={Boolean(updatingTaskId)} disabled={!editingStatusRow || Boolean(updatingTaskId)} onClick={() => editingStatusRow && void updateStatus(editingStatusRow, draftStatus, statusDialogNote)} />
      </div>}
    >
      <InputTextarea value={statusDialogNote} onChange={(event) => setStatusDialogNote(event.target.value)} placeholder="Add a note" rows={3} className="w-full" disabled={Boolean(updatingTaskId)} autoFocus />
    </Dialog>
    <Dialog visible={exportDialogVisible} modal header={renderSurfaceHeader(assignedToMe ? "Export My Activity" : "Export Facility Activity", "Choose a week and report layout for facility task activity.", "pi pi-file-export")} style={{ width: "min(94vw, 42rem)" }} onHide={() => !exporting && setExportDialogVisible(false)} footer={<div className="flex justify-content-end gap-2"><Button type="button" label="Close" severity="secondary" outlined onClick={() => setExportDialogVisible(false)} /><Button type="button" label="Generate" icon="pi pi-file-export" severity="warning" rounded loading={exporting} onClick={() => void exportReport()} /></div>}>
      <div className="flex flex-column gap-3">
        <div><label className="block mb-2 font-medium" htmlFor="staff-export-week">Week of</label><Calendar id="staff-export-week" value={new Date(`${exportWeekStart}T12:00:00`)} onChange={(event) => event.value && setExportWeekStart(getWeekStartKey(dateKeyFromDate(event.value as Date)))} dateFormat="yy-mm-dd" showIcon readOnlyInput className="w-full" /></div>
        <div><label className="block mb-2 font-medium" htmlFor="staff-export-view">Report view</label><Dropdown id="staff-export-view" value={exportView} options={[{ label: "Grid", value: "grid" }, { label: "List", value: "list" }]} optionLabel="label" optionValue="value" onChange={(event) => setExportView(event.value)} className="w-full" /></div>
        <div style={appMutedTextStyle}>{assignedToMe ? "Only your assigned facility task activity will be included." : "Facility task activity for this facility will be included."}</div>
      </div>
    </Dialog>
  </div>;
};

const StaffFacilityTasksWorkspacePage = () => {
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const handleTaskAvailability = (event: Event) => {
      const detail = (event as CustomEvent<{ pendingCount?: number }>).detail;
      setPendingCount(Number(detail?.pendingCount || 0));
    };
    window.addEventListener("facility-tasks:availability", handleTaskAvailability);
    return () => window.removeEventListener("facility-tasks:availability", handleTaskAvailability);
  }, []);

  return <div className="container-fluid p-3 facility-tasks-page">
    <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem", marginBottom: "1rem" }}>
      <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
        <div style={{ minWidth: 0 }}>{renderSurfaceHeader("Facility Tasks", "Review and update tasks assigned to you.", "pi pi-check-square")}</div>
        <div className="flex align-items-center justify-content-end gap-2 flex-wrap" style={{ marginLeft: "auto" }}>
          <Tag value={`Pending: ${pendingCount}`} severity="warning" rounded />
        </div>
      </div>
    </div>
    <TabView className="resident-workspace-tabs" scrollable>
      <TabPanel header="Today's Pass">
        <StaffFacilityTasksPage assignedToMe showReminder={false} />
      </TabPanel>
    </TabView>
  </div>;
};

const SharedFacilityTasksPage = () => {
  const { authStore } = useContext(MobXProviderContext);
  const roleType = String(authStore?.getUserRoleType?.() || "").trim().toLowerCase();
  if (roleType === ROLE_TYPE_PROVIDER || authStore?.isAdmin?.()) {
    return <FacilityTasksPage />;
  }
  if (roleType === ROLE_TYPE_NURSE || roleType === ROLE_TYPE_MHP) {
    return <FacilityTasksPage sharedMode />;
  }
  return <StaffFacilityTasksWorkspacePage />;
};

export default SharedFacilityTasksPage;
